  <admin-nav-bar>
    <!-- If the current user has no visible admin nav items (e.g. CUSTOMER_ADMIN
         whose surfaces are all embedded in Customer 360 tabs) there's nothing
         to render — hide the whole bar rather than showing an empty shell. -->
    <virtual if="{hasAnyVisibleItems()}">
    <!-- LG+ Screens (1024px+): Category-based hover navigation with breadcrumb for active.
         Categories are visually separated by thin vertical dividers so the
         top-level groupings read as distinct clusters instead of a continuous
         row of buttons — helps admins scan and locate the right section. -->
    <div class="hidden lg:block bg-gray-100 shadow-sm border-b border-gray-200 mb-4 relative z-10">
        <div class="flex items-center gap-1 px-4 py-2 flex-wrap">

            <!-- Category navigation buttons with hover dropdowns -->
            <template each="{(category, idx) in state.allVisibleCategories}">
                <!-- Vertical divider between category clusters. Skipped
                     before the first category. Uses a full-height rule so
                     the separation reads as "these are separate groups"
                     rather than just adding a bigger gap. -->
                <span if="{idx > 0}" class="admin-nav-divider" aria-hidden="true"></span>

                <div class="admin-dropdown-item flex-shrink-0"
                     data-category-id="{category.id}"
                     onkeydown="{handleDropdownKeydown}"
                     onfocusout="{handleDropdownFocusout}">
                    <!-- Single-item categories are directly clickable -->
                    <button if="{category.items.length === 1}" onclick="{() => navigateToPage(category.items[0].route)}" class="btn btn-sm gap-1 cursor-pointer {isActive(category.items[0].route) ? 'btn-primary' : 'btn-ghost'} whitespace-nowrap">
                        <i if="{category.items[0].icon}" class="{category.items[0].icon}"></i>
                        <span if="{!category.items[0].icon}" class="text-sm">{category.emoji}</span>
                        <span class="text-sm flex items-center gap-2">
                            <span>{category.items[0].label}</span>
                            <i if="{isSystemAdminOnly(category.items[0])}" class="icon-wrench text-xs opacity-50" title="{locale?.t('admin_nav_sysadminonly')}"></i>
                        </span>
                    </button>

                    <!-- Multi-item categories show dropdown -->
                    <button if="{category.items.length > 1}"
                            type="button"
                            onclick="{(e) => toggleDropdown(e.currentTarget)}"
                            aria-haspopup="menu"
                            aria-expanded="false"
                            aria-controls="admin-dropdown-menu-{category.id}"
                            class="btn btn-sm gap-1 cursor-pointer {isActiveCategory(category) ? 'btn-primary' : 'btn-ghost'} {isActiveCategory(category) ? '' : 'whitespace-nowrap'}">
                        <i if="{category.icon && !isActiveCategory(category)}" class="{category.icon}"></i>
                        <span if="{!category.icon && !isActiveCategory(category)}" class="text-sm">{category.emoji}</span>
                        <i if="{isActiveCategory(category)}" class="{getActiveItemIcon()} me-1"></i>
                        <span class="text-sm">{isActiveCategory(category) ? getBreadcrumbLabel() : category.label}</span>
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                    </button>

                    <ul if="{category.items.length > 1}"
                        id="admin-dropdown-menu-{category.id}"
                        class="admin-dropdown-menu menu p-2 shadow bg-base-100 rounded-box w-52 z-[100]">
                        <template each="{item in category.items}">
                            <li>
                                <button type="button" onclick="{() => navigateToPage(item.route)}" class="{isActive(item.route) ? 'active' : ''}">
                                    <i class="{item.icon}"></i>
                                    <span class="flex items-center gap-2">
                                        <span>{item.label}</span>
                                        <i if="{isSystemAdminOnly(item)}" class="icon-wrench text-xs opacity-50" title="{locale?.t('admin_nav_sysadminonly')}"></i>
                                    </span>
                                </button>
                            </li>
                        </template>
                    </ul>
                </div>
            </template>
        </div>
    </div>
    
    <!-- MD Screens (768-1023px): Show icons only with tooltips -->
    <div class="hidden md:block lg:hidden bg-gray-100 shadow-sm border-b border-gray-200 mb-4">
        <div class="flex items-center gap-1 px-4 py-2">
            <button
                each="{item in state.mdVisibleItems}"
                class="btn btn-sm {isActive(item.route) ? 'btn-primary' : 'btn-ghost'} gap-1 whitespace-nowrap flex-shrink-0 tooltip tooltip-bottom"
                data-tip="{item.label}"
                aria-label="{item.label}"
                onclick="{() => navigateToPage(item.route)}">
                <i class="{item.icon} text-base"></i>
            </button>
            
            <!-- More dropdown for overflow items with categories -->
            <div if="{state.mdOverflowItems.length > 0}" class="dropdown dropdown-end flex-shrink-0">
                <button type="button" tabindex="0" class="btn btn-sm btn-ghost gap-1 cursor-pointer">
                    <span class="text-sm">{getBreadcrumbLabel()}</span>
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                </button>
                <ul tabindex="0" class="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-52 mt-1 z-[100]">
                    <!-- Grouped items by category -->
                    <template each="{category in state.overflowCategories}">
                        <div class="category-group-mobile">
                            <li class="menu-title">
                                <span class="text-xs flex items-center gap-1"><i class="{category.icon}"></i> {category.label}</span>
                            </li>
                            <template each="{item in category.items}">
                                <li>
                                    <button type="button" onclick="{() => navigateToPage(item.route)}" class="{isActive(item.route) ? 'active' : ''} text-xs py-1 ps-6 pe-2 min-h-0">
                                        <i class="{item.icon}"></i>
                                        <span>{item.label}</span>
                                    </button>
                                </li>
                            </template>
                        </div>
                    </template>
                </ul>
            </div>
        </div>
    </div>

    <!-- Mobile Navigation with Dropdown and category support -->
    <div class="md:hidden bg-gray-100 shadow-sm border-b border-gray-200 mb-4">
        <div class="dropdown w-full">
            <button type="button" tabindex="0" class="btn btn-sm btn-ghost w-full justify-between rounded-none cursor-pointer">
                <span class="flex items-center gap-2 text-sm">
                    <i class="{getCurrentIcon()}"></i>
                    <span>{getBreadcrumbLabel()}</span>
                </span>
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
            </button>
            <ul tabindex="0" class="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-full max-h-96 overflow-y-auto z-[100]">

                <!-- Grouped items by category -->
                <template each="{category in state.allVisibleCategories}">
                    <div class="category-group-mobile">
                        <li class="menu-title mobile-menu-title">
                            <span class="text-xs flex items-center gap-1"><i class="{category.icon}"></i> {category.label}</span>
                        </li>
                        <template each="{item in category.items}">
                            <li>
                                <button
                                    type="button"
                                    onclick="{() => navigateToPage(item.route)}"
                                    class="{isActive(item.route) ? 'active' : ''} text-xs py-1 ps-6 pe-2 min-h-0">
                                    <i class="{item.icon}"></i>
                                    <span>{item.label}</span>
                                </button>
                            </li>
                        </template>
                    </div>
                </template>
            </ul>
        </div>
    </div>
    </virtual>

    <script>
        import { getFeatureFlagService } from '../../services/service-loader';

        export default {
            state: {
                currentRoute: '',
                userContext: null,
                featureFlags: {},
                categories: [],
                
                // Filtered categories for different breakpoints
                visibleCategories: [],
                allVisibleCategories: [],
                flattenedOverflowMenu: [],
                flattenedAllMenu: [],
                
                // XL breakpoint (1280-1535px): 9 items
                xlVisibleItems: [],
                xlOverflowItems: [],
                maxXlVisibleItems: 9,
                overflowCategories: [],
                
                // LG breakpoint (1024-1279px): 7 items
                lgVisibleItems: [],
                lgOverflowItems: [],
                maxLgVisibleItems: 7,
                
                // MD breakpoint (768-1023px): 14 items (icons only, so can fit more)
                mdVisibleItems: [],
                mdOverflowItems: [],
                maxMdVisibleItems: 14
            },

            async onMounted() {
                await this.loadUserContext();
                await this.loadFeatureFlags();
                // The nav bar is a SYSTEM_ADMIN surface (non-admins see
                // hasAnyVisibleItems() return false and the whole bar
                // hides). Category labels that are truly admin-only —
                // like "Users & Activity" covering cross-customer
                // investigation surfaces — live in the system-admin
                // locale chunk. Load it here so every admin page that
                // embeds this component picks the phrases up without
                // each page needing its own load call. Non-admin
                // sessions skip the download entirely.
                if (this.state.userContext?.role === 'SYSTEM_ADMIN') {
                    try {
                        if (this.locale?.loadSystemAdminMessages) {
                            await this.locale.loadSystemAdminMessages();
                        }
                    } catch (err) {
                        console.warn('[AdminNavBar] Failed to load system-admin phrases:', err);
                    }
                }
                this.initializeCategories();
                this.filterVisibleItems();
                this.setCurrentRoute();
                this.setupDropdownHoverAreas();

                // Listen for route changes
                window.addEventListener('hashchange', () => this.setCurrentRoute());
            },

            initializeCategories() {
                // METALARK-694: 4 → 5 categories. "Customers" used to carry
                // both customer-management pages AND cross-customer
                // investigation surfaces (Global Users, Global Audit Logs);
                // the latter weren't discoverable from the name. Split into
                // "Customers" (customer-management work) and
                // "Users & Activity" (cross-customer admin investigation)
                // so each label accurately describes what lives behind it.
                this.state.categories = [
                    {
                        id: 'dashboard',
                        label: this.locale?.t('admin_nav_dashboard'),
                        emoji: '📊',
                        icon: 'icon-metalark-settings',
                        items: [
                            {
                                id: 'dashboard',
                                label: this.locale?.t('admin_nav_dashboard'),
                                icon: 'icon-metalark-settings',
                                route: 'admin',
                                // Admin landing dashboard shows system-wide metrics
                                // and is a SYSTEM_ADMIN surface. CUSTOMER_ADMINs land
                                // on their Customer 360 or tenant pages directly.
                                permission: 'adminDashboard'
                            }
                        ]
                    },
                    this.buildAdministrationCategory(),
                    {
                        // The customer list + Customer 360 drill-down now lives
                        // on the top-level "Customer Administration" button;
                        // this dropdown is reserved for cross-customer
                        // operational surfaces (metrics, notifications, quota
                        // health, deletions).
                        id: 'customer-operations',
                        label: this.locale?.t('admin_nav_customeroperations'),
                        icon: 'icon-metalark-chart',
                        items: [
                            {
                                id: 'metrics',
                                label: this.locale?.t('admin_nav_metrics'),
                                icon: 'icon-metalark-chart',
                                route: 'metrics',
                                // Subscription metrics (METALARK-671) — revenue,
                                // conversion, churn. SYSTEM_ADMIN only; uses the
                                // same customers:manage gate as the dashboard KPIs.
                                permission: 'customerManagement'
                            },
                            {
                                // Moved from System to Customers — notifications
                                // are per-customer email flows (grace-period
                                // warnings, deletion notices, etc.).
                                id: 'notifications',
                                label: this.locale?.t('admin_nav_notifications'),
                                icon: 'icon-metalark-clock',
                                route: 'notification-dashboard',
                                permission: 'notificationManagement'
                            },
                            {
                                // Moved from System to Customers — quota usage
                                // and remaining credits are customer-facing
                                // resource signals, not infrastructure health.
                                id: 'quota-health',
                                label: this.locale?.t('admin_nav_quotahealth'),
                                icon: 'icon-metalark-pulse',
                                route: 'quota-health',
                                permission: 'systemHealth'
                            },
                            {
                                // Per-resource processing diagnostics: the job tree an
                                // upload produced, why anything failed, and what it
                                // cost. Sits beside quota-health as admin tooling.
                                id: 'xray',
                                label: this.locale?.t('admin_nav_xray'),
                                icon: 'icon-metalark-search',
                                route: 'xray',
                                permission: 'systemHealth'
                            },
                            {
                                id: 'customer-deletions',
                                label: this.locale?.t('admin_nav_deletions'),
                                icon: 'icon-bin',
                                route: 'customer-deletions',
                                permission: 'customerDeletionManagement'
                            },
                            {
                                // Cross-customer domain management — the
                                // "(All)" suffix mirrors the "(Global)"
                                // marker on Users & Activity items so the
                                // scope is obvious at a glance. Customer-
                                // scoped domain settings still live inside
                                // Customer 360 > Settings.
                                id: 'custom-domains',
                                label: this.locale?.t('admin_nav_customdomains'),
                                icon: 'icon-metalark-link',
                                route: 'custom-domains',
                                permission: 'customDomainManagement'
                            },
                            {
                                // Marketing campaign codes (trial access +
                                // Stripe discount codes). SYSTEM_ADMIN only
                                // — reuses the customers:manage gate since
                                // campaigns drive the trial-signup funnel
                                // and attribution reporting.
                                id: 'campaigns',
                                label: this.locale?.t('admin_nav_campaigns'),
                                icon: 'icon-metalark-tag',
                                route: 'campaigns',
                                permission: 'customerManagement'
                            },
                            {
                                // METALARK-929: reseller/agency partner management
                                // (create/suspend, link customers, assign admins).
                                // SYSTEM_ADMIN only, via `partnerManagement`.
                                id: 'partners',
                                label: 'Partners',
                                icon: 'icon-business',
                                route: 'partners',
                                activeRoutes: ['partners'],
                                permission: 'partnerManagement'
                            }
                        ]
                    },
                    {
                        id: 'users-activity',
                        label: this.locale?.t('admin_nav_users_activity'),
                        icon: 'icon-metalark-user',
                        emoji: '🔍',
                        items: [
                            {
                                id: 'users',
                                label: this.locale?.t('admin_nav_users_global'),
                                icon: 'icon-metalark-user',
                                route: 'users',
                                // Global users view is a SYSTEM_ADMIN surface — shows
                                // users across every customer. CUSTOMER_ADMINs manage
                                // users from their customer's own Users tab instead.
                                permission: 'globalUserManagement'
                            },
                            {
                                // Activity Dashboard moved here from System:
                                // aggregated user activity reads more as a
                                // Users & Activity surface than an infra one.
                                id: 'activity-dashboard',
                                label: this.locale?.t('admin_nav_activitydashboard'),
                                icon: 'icon-metalark-chart',
                                route: 'activity-dashboard',
                                permission: 'systemHealth'
                            },
                            {
                                id: 'audit-logs',
                                label: this.locale?.t('admin_nav_auditlogs_global'),
                                icon: 'icon-metalark-shield',
                                route: 'audit-logs',
                                // Global audit log is system-wide and unredacted —
                                // SYSTEM_ADMIN only. CUSTOMER_ADMINs see the redacted
                                // customer-scoped slice via Customer 360's Audit tab.
                                permission: 'globalAuditLog'
                            }
                        ]
                    },
                    {
                        id: 'system',
                        label: this.locale?.t('admin_nav_systemconfiguration'),
                        emoji: '⚙️',
                        icon: 'icon-metalark-settings',
                        items: [
                            {
                                id: 'feature-flags',
                                label: this.locale?.t('admin_nav_featureflags'),
                                icon: 'icon-metalark-zap',
                                route: 'feature-flags',
                                permission: 'featureFlagManagement'
                            },
                            {
                                // Outbound webhook delivery log (METALARK-271).
                                // SYSTEM_ADMIN-only — the log is cross-customer
                                // and exposes raw outbound payloads + endpoints.
                                id: 'webhook-deliveries',
                                label: this.locale?.t('admin_nav_webhookdeliveries'),
                                icon: 'icon-metalark-link',
                                route: 'webhook-deliveries',
                                permission: 'webhookDeliveryManagement'
                            },
                            {
                                // Webhook subscription + rule configuration
                                // (METALARK-271). SYSTEM_ADMIN-only — manages
                                // destinations, URL rotation in SSM, and rules
                                // (event pattern + sampling + rate-limit).
                                id: 'webhook-subscriptions',
                                label: this.locale?.t('admin_nav_webhooksubscriptions'),
                                icon: 'icon-metalark-settings',
                                route: 'webhook-subscriptions',
                                permission: 'webhookSubscriptionManagement'
                            },
                            {
                                // Stripe Setup status + rule reconcile
                                // (METALARK-671). SYSTEM_ADMIN-only — surfaces
                                // SSM params, partner-bus association, and the
                                // four EventBridge rules so misconfigured stacks
                                // don't stay silent.
                                id: 'stripe-setup',
                                label: this.locale?.t('admin_nav_stripesetup'),
                                icon: 'icon-metalark-settings',
                                route: 'stripe-setup',
                                permission: 'stripeSetup'
                            },
                            {
                                // Repository Providers Setup: read-only view of
                                // each repository provider's OAuth client
                                // credentials in SSM, so operators can see which
                                // providers are configured vs still placeholders.
                                id: 'repository-providers-setup',
                                label: this.locale?.t('admin_nav_repositoryproviderssetup'),
                                icon: 'icon-metalark-settings',
                                route: 'repository-providers-setup',
                                permission: 'repositoryProvidersSetup'
                            }
                        ]
                    },
                    {
                        id: 'email-communications',
                        label: this.locale?.t('admin_nav_email'),
                        icon: 'icon-metalark-mail',
                        items: [
                            {
                                id: 'email-templates',
                                label: this.locale?.t('admin_nav_emailtemplates'),
                                icon: 'icon-metalark-edit',
                                route: 'email-templates',
                                permission: 'emailManagement'
                            },
                            {
                                id: 'email-suppression',
                                label: this.locale?.t('admin_nav_emailsuppression'),
                                icon: 'icon-metalark-x',
                                route: 'email-suppression-list',
                                permission: 'emailManagement'
                            },
                            {
                                // Email Logs in the admin nav is the Global
                                // cross-customer view — mirrors the pattern
                                // of "Users (Global)" and "Audit Logs (Global)"
                                // above. The nav bar only renders for
                                // SYSTEM_ADMIN so the wrench marker + the
                                // "(Global)" suffix reflect the actual
                                // scope of this surface.
                                id: 'email-logs',
                                label: this.locale?.t('admin_nav_emaillogs_global'),
                                icon: 'icon-metalark-list',
                                route: 'email-logs',
                                permission: 'emailLogs'
                            }
                        ]
                    }
                ];
            },

            setupDropdownHoverAreas() {
                // Delay setup to ensure DOM is fully rendered
                setTimeout(() => {
                    const dropdowns = document.querySelectorAll('.admin-dropdown-item');
                    const dropdownData = [];
                    let zIndexCounter = 50;
                    
                    // Single global listener for mouse position
                    let lastMouseX = 0;
                    let lastMouseY = 0;
                    
                    // Global mousemove to detect zone exits even on z-index overlap
                    document.addEventListener('mousemove', (e) => {
                        lastMouseX = e.clientX;
                        lastMouseY = e.clientY;
                        
                        // Check all open dropdowns
                        dropdownData.forEach(data => {
                            if (data.content.style.display === 'block') {
                                // Use button and content bounds to create tighter zone
                                const buttonRect = data.button.getBoundingClientRect();
                                const contentRect = data.content.getBoundingClientRect();
                                
                                const minLeft = Math.min(buttonRect.left, contentRect.left) - 25;
                                const maxRight = Math.max(buttonRect.right, contentRect.right) + 25;
                                const minTop = Math.min(buttonRect.top, contentRect.top) - 20;
                                const maxBottom = Math.max(buttonRect.bottom, contentRect.bottom) + 20;
                                
                                const inZone = lastMouseX >= minLeft && lastMouseX <= maxRight &&
                                               lastMouseY >= minTop && lastMouseY <= maxBottom;
                                
                                if (!inZone && !data.closeScheduled) {
                                    // We're outside the zone - schedule a close
                                    data.closeScheduled = true;
                                    data.closeTimeout = setTimeout(() => {
                                        data.content.style.display = 'none';
                                        data.content.style.visibility = 'hidden';
                                        data.content.style.opacity = '0';
                                        data.button.setAttribute('aria-expanded', 'false');
                                        data.closeScheduled = false;
                                    }, 300);
                                } else if (inZone && data.closeScheduled) {
                                    // We're back in the zone - cancel the close
                                    clearTimeout(data.closeTimeout);
                                    data.closeScheduled = false;
                                }
                            }
                        });
                    });
                    
                    dropdowns.forEach(dropdown => {
                        const button = dropdown.querySelector('button');
                        const content = dropdown.querySelector('.admin-dropdown-menu');
                        
                        if (!button || !content) return;
                        
                        const data = { dropdown, button, content, closeTimeout: null, closeScheduled: false };
                        dropdownData.push(data);
                        
                        // Show menu on button hover
                        button.addEventListener('mouseenter', () => {
                            // Close all other menus instantly when entering a different button
                            dropdownData.forEach(d => {
                                if (d !== data && d.content.style.display === 'block') {
                                    d.content.style.display = 'none';
                                    d.content.style.visibility = 'hidden';
                                    d.content.style.opacity = '0';
                                    d.button.setAttribute('aria-expanded', 'false');
                                    clearTimeout(d.closeTimeout);
                                    d.closeScheduled = false;
                                }
                            });

                            clearTimeout(data.closeTimeout);
                            data.closeScheduled = false;
                            content.style.display = 'block';
                            content.style.visibility = 'visible';
                            content.style.opacity = '1';
                            content.style.zIndex = ++zIndexCounter;
                            button.setAttribute('aria-expanded', 'true');
                        });

                        // Keep menu open when hovering over it
                        content.addEventListener('mouseenter', () => {
                            clearTimeout(data.closeTimeout);
                            data.closeScheduled = false;
                            content.style.display = 'block';
                            content.style.visibility = 'visible';
                            content.style.opacity = '1';
                            content.style.zIndex = ++zIndexCounter;
                            button.setAttribute('aria-expanded', 'true');
                        });
                    });

                }, 100);
            },

            // Keyboard toggle: click handler on the dropdown trigger button.
            // Opens/closes the menu and keeps aria-expanded in sync. Mouse
            // users get the hover behavior set up by setupDropdownHoverAreas;
            // keyboard users get this path via Enter/Space on the button.
            toggleDropdown(buttonEl) {
                const container = buttonEl.closest('.admin-dropdown-item');
                if (!container) return;
                const content = container.querySelector('.admin-dropdown-menu');
                if (!content) return;
                const isOpen = content.style.display === 'block';
                if (isOpen) {
                    this.closeDropdownEl(content, buttonEl);
                } else {
                    // Close any other open dropdowns first so we don't
                    // stack menus on top of each other.
                    document.querySelectorAll('.admin-dropdown-menu').forEach(other => {
                        if (other !== content && other.style.display === 'block') {
                            const otherButton = other.closest('.admin-dropdown-item')?.querySelector('button[aria-haspopup]');
                            this.closeDropdownEl(other, otherButton);
                        }
                    });
                    this.openDropdownEl(content, buttonEl);
                }
            },

            openDropdownEl(content, buttonEl) {
                content.style.display = 'block';
                content.style.visibility = 'visible';
                content.style.opacity = '1';
                if (buttonEl) buttonEl.setAttribute('aria-expanded', 'true');
            },

            closeDropdownEl(content, buttonEl) {
                content.style.display = 'none';
                content.style.visibility = 'hidden';
                content.style.opacity = '0';
                if (buttonEl) buttonEl.setAttribute('aria-expanded', 'false');
            },

            // Escape closes the dropdown and returns focus to the trigger
            // button, matching the WAI-ARIA menu-button pattern.
            handleDropdownKeydown(e) {
                if (e.key !== 'Escape') return;
                const container = e.currentTarget;
                const button = container.querySelector('button[aria-haspopup]');
                const content = container.querySelector('.admin-dropdown-menu');
                if (!content || content.style.display !== 'block') return;
                e.preventDefault();
                this.closeDropdownEl(content, button);
                if (button) button.focus();
            },

            // When focus leaves the dropdown container entirely (Tab out
            // of the last item, Shift+Tab off the trigger), close the
            // menu. setTimeout lets focus settle so we can read the new
            // activeElement; if it's still inside the container (focus
            // moved between trigger and items), keep the menu open.
            handleDropdownFocusout(e) {
                const container = e.currentTarget;
                setTimeout(() => {
                    if (!container.contains(document.activeElement)) {
                        const button = container.querySelector('button[aria-haspopup]');
                        const content = container.querySelector('.admin-dropdown-menu');
                        if (content && content.style.display === 'block') {
                            this.closeDropdownEl(content, button);
                        }
                    }
                }, 0);
            },

            onUnmounted() {
                window.removeEventListener('hashchange', () => this.setCurrentRoute());
            },

            // Top-level "Administration" entry that takes the viewer into the
            // Customer 360 hub. Label + route differ by role:
            //   - CUSTOMER_ADMIN: "Administration", routes straight to their
            //     own Customer 360 page (they only have one customer).
            //   - SYSTEM_ADMIN: "Customer Administration", routes to the
            //     customers list so they can pick which customer to administer.
            // activeRoutes on the item lets us highlight this category while
            // the admin is drilled into any Customer 360 tab without having
            // to encode customerId in the route-matching logic.
            buildAdministrationCategory() {
                const role = this.state.userContext?.role;
                const customerId = this.state.userContext?.customerId;
                const isCustomerAdmin = role === 'CUSTOMER_ADMIN' && customerId;
                // METALARK-929: a PARTNER_ADMIN does NOT use this bar — its portal
                // nav (Dashboard / Customers / Webhooks) lives in the main navbar,
                // and hasAnyVisibleItems() hides this bar for partners entirely.
                const label = isCustomerAdmin
                    ? this.locale?.t('admin_nav_administration')
                    : this.locale?.t('admin_nav_customeradministration');
                const route = isCustomerAdmin
                    ? `customer-360?customerId=${encodeURIComponent(customerId)}`
                    : 'customers';
                return {
                    id: 'administration',
                    label,
                    icon: 'icon-metalark-clipboard',
                    items: [
                        {
                            id: 'administration',
                            label,
                            icon: 'icon-metalark-clipboard',
                            route,
                            activeRoutes: ['customer-360', 'customers'],
                            permission: 'administration'
                        }
                    ]
                };
            },

            async loadUserContext() {
                try {
                    const userContext = await this.tools.getUserContext();
                    this.update({ userContext });
                } catch (error) {
                    console.error('[AdminNavBar] Failed to load user context for admin nav:', error);
                    this.update({ userContext: {} });
                }
            },

            async loadFeatureFlags() {
                try {
                    const featureFlagService = await getFeatureFlagService();
                    const response = await featureFlagService.getActiveFlags(false, { skipLoading: true });
                    const flags = response.flags;
                    this.update({ featureFlags: flags });
                } catch (error) {
                    console.error('[AdminNavBar] Failed to load feature flags:', error);
                    this.update({ featureFlags: {} });
                }
            },

            filterVisibleItems() {
                // Filter categories based on permissions AND feature flags
                const visibleCategories = this.state.categories.map(category => {
                    const visibleItems = category.items.filter(item => {
                        // Check permission first
                        if (item.permission && !this.canSeeComponent(item.permission)) {
                            return false;
                        }

                        // Check feature flag if specified
                        if (item.featureFlag) {
                            const isEnabled = this.state.featureFlags[item.featureFlag];
                            return isEnabled === true;
                        }

                        return true;
                    });

                    return {
                        ...category,
                        items: visibleItems
                    };
                }).filter(category => category.items.length > 0);
                
                // Create flat list of all visible items for old logic
                const allVisibleItems = visibleCategories.flatMap(cat => cat.items);
                
                // Split into visible and overflow for each breakpoint
                const xlVisibleItems = allVisibleItems.slice(0, this.state.maxXlVisibleItems);
                const xlOverflowItems = allVisibleItems.slice(this.state.maxXlVisibleItems);
                
                const lgVisibleItems = allVisibleItems.slice(0, this.state.maxLgVisibleItems);
                const lgOverflowItems = allVisibleItems.slice(this.state.maxLgVisibleItems);
                
                const mdVisibleItems = allVisibleItems.slice(0, this.state.maxMdVisibleItems);
                const mdOverflowItems = allVisibleItems.slice(this.state.maxMdVisibleItems);
                
                // Get overflow categories by filtering for items in overflow
                const overflowRoutes = new Set(xlOverflowItems.map(item => item.route));
                const overflowCategories = visibleCategories
                    .map(category => ({
                        ...category,
                        items: category.items.filter(item => overflowRoutes.has(item.route))
                    }))
                    .filter(category => category.items.length > 0);
                
                this.update({
                    visibleCategories,
                    allVisibleCategories: visibleCategories,
                    xlVisibleItems,
                    xlOverflowItems,
                    lgVisibleItems,
                    lgOverflowItems,
                    mdVisibleItems,
                    mdOverflowItems,
                    overflowCategories,
                    flattenedOverflowMenu: this.getFlattenedMenu(overflowCategories),
                    flattenedAllMenu: this.getFlattenedMenu(visibleCategories)
                });
            },

            setCurrentRoute() {
                const hash = window.location.hash || '#admin';
                const route = hash.replace(/^#\/?/, '').split('?')[0];
                this.update({ currentRoute: route });
            },

            isActive(route) {
                const current = this.state.currentRoute;
                if (!current) return false;
                if (current === route) return true;
                if (current.startsWith(route + '/') || current.startsWith(route + '?')) return true;
                // Drill-down / related routes declared as `activeRoutes` on the nav item
                // should also highlight this item (e.g. customer-360 highlights Customers).
                for (const cat of (this.state.categories || [])) {
                    for (const item of (cat.items || [])) {
                        if (item.route !== route) continue;
                        for (const related of (item.activeRoutes || [])) {
                            if (current === related
                                || current.startsWith(related + '/')
                                || current.startsWith(related + '?')) {
                                return true;
                            }
                        }
                    }
                }
                return false;
            },

            navigateToPage(route) {
                this.tools.navigateTo(route);
                if (document.activeElement) {
                    document.activeElement.blur();
                }
            },

            getCurrentIcon() {
                const current = this.findCurrentItem();
                return current?.icon || 'icon-metalark-settings';
            },

            getActiveItemIcon() {
                const current = this.findCurrentItem();
                return current?.icon || 'icon-metalark-settings';
            },

            getBreadcrumbLabel() {
                const current = this.findCurrentItem();
                if (!current) return this.locale?.t('admin_nav_dashboard');

                // Find the category for this item
                const category = this.state.allVisibleCategories.find(cat =>
                    cat.items.some(item => item.route === current.route)
                );

                if (!category) return current.label;

                // For dashboard, just show "Dashboard"
                if (current.route === 'admin') return this.locale?.t('admin_nav_dashboard');

                // For other items, show "CATEGORY > Item"
                return `${category.label} > ${current.label}`;
            },

            findCurrentItem() {
                for (const category of this.state.allVisibleCategories) {
                    const item = category.items.find(item => this.isActive(item.route));
                    if (item) return item;
                }
                return null;
            },

            canSeeComponent(componentName) {
                const role = this.state.userContext?.role;
                if (!role) return false;
                
                const visibility = {
                    adminDashboard: ['SYSTEM_ADMIN'].includes(role),
                    administration: ['SYSTEM_ADMIN', 'CUSTOMER_ADMIN'].includes(role),
                    customerManagement: ['SYSTEM_ADMIN'].includes(role),
                    customerDeletionManagement: ['SYSTEM_ADMIN'].includes(role),
                    globalUserManagement: ['SYSTEM_ADMIN'].includes(role),
                    globalAuditLog: ['SYSTEM_ADMIN'].includes(role),
                    identityProviderManagement: ['SYSTEM_ADMIN', 'CUSTOMER_ADMIN'].includes(role),
                    customDomainManagement: ['SYSTEM_ADMIN'].includes(role),
                    loginDomainSettings: ['SYSTEM_ADMIN', 'CUSTOMER_ADMIN'].includes(role),
                    emailManagement: ['SYSTEM_ADMIN'].includes(role),
                    // Global email logs are cross-customer and unredacted;
                    // CUSTOMER_ADMINs reach their own customer's email logs
                    // through the Customer 360 Audit tab instead.
                    emailLogs: ['SYSTEM_ADMIN'].includes(role),
                    billingManagement: ['SYSTEM_ADMIN', 'CUSTOMER_ADMIN'].includes(role),
                    notificationManagement: ['SYSTEM_ADMIN'].includes(role),
                    featureFlagManagement: ['SYSTEM_ADMIN'].includes(role),
                    repositoryManagement: ['SYSTEM_ADMIN', 'CUSTOMER_ADMIN'].includes(role),
                    systemHealth: ['SYSTEM_ADMIN'].includes(role),
                    // Outbound webhook delivery log (METALARK-271). SYSTEM_ADMIN
                    // only — the log spans every customer and includes raw
                    // outbound payloads and integration endpoints.
                    webhookDeliveryManagement: ['SYSTEM_ADMIN'].includes(role),
                    // Webhook subscription + rule configuration (METALARK-271,
                    // opened to customer admins in METALARK-928). CUSTOMER_ADMIN
                    // sees and manages only their own customer's subscriptions;
                    // the service layer + UI scope everything to their customer
                    // and hide system-owned rows / infra dispatch / log links.
                    // (METALARK-929: a PARTNER_ADMIN reaches webhooks via the main
                    // navbar's partner cluster, not this bar — so it's not listed here.)
                    webhookSubscriptionManagement: ['SYSTEM_ADMIN', 'CUSTOMER_ADMIN'].includes(role),
                    // METALARK-929: SYSTEM_ADMIN reseller/agency partner management.
                    partnerManagement: ['SYSTEM_ADMIN'].includes(role),
                    stripeSetup: ['SYSTEM_ADMIN'].includes(role),
                    repositoryProvidersSetup: ['SYSTEM_ADMIN'].includes(role)
                };

                return visibility[componentName] || false;
            },

            isSystemAdminOnly(item) {
                // Check if item is restricted to SYSTEM_ADMIN only
                const sysAdminOnlyPermissions = ['adminDashboard', 'customerManagement', 'customerDeletionManagement',
                                                 'globalUserManagement', 'globalAuditLog', 'emailLogs',
                                                 'customDomainManagement', 'emailManagement',
                                                 'notificationManagement', 'featureFlagManagement', 'systemHealth',
                                                 'webhookDeliveryManagement', 'stripeSetup',
                                                 'repositoryProvidersSetup'];

                return item.permission && sysAdminOnlyPermissions.includes(item.permission) || !!item.featureFlag;
            },

            getFlattenedMenu(categories) {
                const flattened = [];
                if (!categories) return flattened;
                for (const category of categories) {
                    // Add category header
                    flattened.push({
                        isCategory: true,
                        icon: category.icon,
                        label: category.label
                    });
                    // Add items for this category
                    for (const item of category.items) {
                        flattened.push({
                            isCategory: false,
                            ...item
                        });
                    }
                }
                return flattened;
            },

            isActiveCategory(category) {
                // Show breadcrumb if any item in this category is active
                return category.items.some(item => this.isActive(item.route));
            },

            // True when this user has at least one nav item visible. When
            // false (e.g. CUSTOMER_ADMIN whose surfaces all live inside
            // Customer 360 tabs), we hide the entire nav bar rather than
            // render an empty shell.
            hasAnyVisibleItems() {
                // METALARK-929: a PARTNER_ADMIN navigates via the main navbar's
                // partner cluster, not this bar — hide it entirely for partners,
                // even on shared admin pages they can reach (e.g. webhook
                // subscriptions). Without this they'd see a stray Administration
                // dropdown (Dashboard / Customers) duplicating the main nav.
                if (this.state.userContext?.role === 'PARTNER_ADMIN') return false;
                return (this.state.allVisibleCategories || []).some(cat => (cat.items || []).length > 0);
            }
        }
    </script>

    <style>
        /* Admin dropdown item styling */
        .admin-dropdown-item {
            position: relative;
        }

        /* Vertical divider between category clusters on the LG nav.
         * Rendered as a thin line scaled slightly shorter than the
         * nav row height so it doesn't touch the top/bottom borders —
         * gives each category group breathing room without adding
         * visual weight that would compete with the buttons. */
        .admin-nav-divider {
            display: inline-block;
            width: 1px;
            height: 1.25rem;
            background-color: hsl(var(--bc) / 0.15);
            margin: 0 0.5rem;
            flex-shrink: 0;
        }
        
        /* Admin dropdown menu styling */
        .admin-dropdown-menu {
            position: absolute;
            top: 100%;
            left: 0;
            display: none;
            visibility: hidden;
            opacity: 0;
            transition: opacity 0.15s ease-out, visibility 0.15s ease-out;
            margin-top: 0;
            min-width: 100%;
            width: max-content;
            z-index: 50;
        }
        
        /* Active state styling */
        .menu li > a.active {
            background-color: hsl(var(--p));
            color: hsl(var(--pc));
            font-weight: 600;
        }
        
        .dropdown-content .menu li > a.active {
            background-color: hsl(var(--p));
            color: hsl(var(--pc)) !important;
            font-weight: 600;
        }
        
        /* Menu title styling for category headers */
        .menu li.menu-title {
            padding: 0.25rem 0;
            margin-top: 0.5rem;
        }

        .menu li.menu-title:first-child {
            margin-top: 0;
        }

        .menu li.menu-title > span {
            color: hsl(var(--bc) / 0.6);
            font-weight: 600;
        }

        /* Category grouping for mobile */
        .category-group-mobile {
            margin-bottom: 0.5rem;
            padding-bottom: 0.5rem;
            border-bottom: 1px solid hsl(var(--bc) / 0.1);
        }

        .category-group-mobile:last-child {
            border-bottom: none;
            margin-bottom: 0;
            padding-bottom: 0;
        }

        /* Tighter mobile menu styling */
        .mobile-menu-title {
            padding: 0.25rem 0.5rem !important;
            margin-top: 0 !important;
            margin-bottom: 0.25rem !important;
        }
        
        /* Smooth scrolling for horizontal nav */
        .overflow-x-auto {
            scrollbar-width: thin;
            scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
        }
        
        .overflow-x-auto::-webkit-scrollbar {
            height: 4px;
        }
        
        .overflow-x-auto::-webkit-scrollbar-track {
            background: transparent;
        }
        
        .overflow-x-auto::-webkit-scrollbar-thumb {
            background-color: rgba(0, 0, 0, 0.2);
            border-radius: 2px;
        }
        
        /* Hide tooltips on large screens where labels are visible */
        @media (min-width: 1024px) {
            .lg\:tooltip-hidden:before,
            .lg\:tooltip-hidden:after {
                display: none !important;
            }
        }
    </style>
</admin-nav-bar>


